/**
 * FLIP-488. Tells the stylesheet when the on-screen keyboard is up while Nova is open, and how much
 * of the screen is left above it.
 *
 * The two mobile platforms disagree about what a keyboard is. Android resizes the window, so the
 * layout viewport and the visual viewport shrink together and fixed elements ride up with them.
 * iOS leaves the layout viewport alone and PANS the visual viewport instead, so fixed elements move
 * but the page behind them does not - which is what left a strip of undimmed page between the nav
 * and the keyboard. Neither exposes "the keyboard is open" directly, so it is inferred: an editable
 * element has focus AND the visible height has dropped well below what it was with nothing focused.
 *
 * The baseline is re-read on every update while nothing is focused, so rotating the device (a
 * legitimate height change with no keyboard) never reads as a keyboard.
 *
 * Takes the window it reads rather than reaching for the global, like nova-voice-support.ts, so the
 * spec can hand it a fake - Karma's Chrome has no on-screen keyboard to open.
 */

/** Class on <html> while the keyboard is up. The stylesheets key off this - see cdk-theme.scss. */
export const KEYBOARD_OPEN_CLASS = "nova-keyboard-open";
/** Height of the visible area above the keyboard. */
export const KEYBOARD_VIEWPORT_HEIGHT_VAR = "--nova-vv-height";
/** How far the visible area has been panned down from the top of the layout viewport (iOS). */
export const KEYBOARD_VIEWPORT_TOP_VAR = "--nova-vv-top";

/**
 * How much shorter than the baseline the visible area has to be before it counts as a keyboard.
 * Real keyboards take 200px+; the URL bar collapsing on scroll is well under 100.
 */
export const KEYBOARD_MIN_HEIGHT_DROP = 120;

/** The slice of `window` this reads. Partial so a spec can describe a browser without all of it. */
export interface KeyboardViewportWindow {
  innerHeight: number;
  /** Optional: absent means "can't tell", which is treated as touch rather than silently disabling. */
  matchMedia?: (query: string) => { matches: boolean };
  visualViewport?: {
    height: number;
    offsetTop: number;
    addEventListener(type: string, listener: () => void): void;
    removeEventListener(type: string, listener: () => void): void;
  } | null;
  document: {
    documentElement: {
      classList: { add(name: string): void; remove(name: string): void; contains(name: string): boolean };
      style: { setProperty(name: string, value: string): void; removeProperty(name: string): void };
    };
    activeElement?: { tagName?: string; isContentEditable?: boolean } | null;
    addEventListener(type: string, listener: () => void): void;
    removeEventListener(type: string, listener: () => void): void;
  };
}

function isEditable(el: { tagName?: string; isContentEditable?: boolean } | null | undefined): boolean {
  const tag = el?.tagName?.toUpperCase();
  return tag === "TEXTAREA" || tag === "INPUT" || el?.isContentEditable === true;
}

/**
 * Starts watching. Returns the stop function, which also clears everything it set - closing Nova
 * with the keyboard up must not leave the nav hidden.
 */
export function watchKeyboardViewport(win: KeyboardViewportWindow, onChange?: (open: boolean) => void): () => void {
  const root = win.document.documentElement;
  const viewport = win.visualViewport;
  let baseline = Math.max(win.innerHeight, viewport?.height ?? 0);
  let open = false;

  const clear = (): void => {
    root.classList.remove(KEYBOARD_OPEN_CLASS);
    root.style.removeProperty(KEYBOARD_VIEWPORT_HEIGHT_VAR);
    root.style.removeProperty(KEYBOARD_VIEWPORT_TOP_VAR);
  };

  const update = (): void => {
    const height = viewport?.height ?? win.innerHeight;
    const focused = isEditable(win.document.activeElement);
    if (!focused) {
      // No keyboard can be up without a focused field, so this height is the keyboard-less one.
      baseline = Math.max(win.innerHeight, height);
    }
    // A desktop window dragged shorter while the textarea has focus is not a keyboard.
    const touch = win.matchMedia?.("(pointer: coarse)").matches ?? true;
    const nowOpen = touch && focused && baseline - height >= KEYBOARD_MIN_HEIGHT_DROP;

    if (nowOpen) {
      root.classList.add(KEYBOARD_OPEN_CLASS);
      root.style.setProperty(KEYBOARD_VIEWPORT_HEIGHT_VAR, `${Math.round(height)}px`);
      root.style.setProperty(KEYBOARD_VIEWPORT_TOP_VAR, `${Math.round(viewport?.offsetTop ?? 0)}px`);
    } else {
      clear();
    }
    if (nowOpen !== open) {
      open = nowOpen;
      onChange?.(open);
    }
  };

  viewport?.addEventListener("resize", update);
  viewport?.addEventListener("scroll", update);
  // focusin/focusout rather than focus/blur: they bubble, so one listener covers every field.
  win.document.addEventListener("focusin", update);
  win.document.addEventListener("focusout", update);

  return () => {
    viewport?.removeEventListener("resize", update);
    viewport?.removeEventListener("scroll", update);
    win.document.removeEventListener("focusin", update);
    win.document.removeEventListener("focusout", update);
    clear();
  };
}
